import { useState, useEffect } from 'react'
import type { InsiderProductType, InsiderCampaignData } from 'src/typings/insider'

export function useInsiderCampaignLoader(
  rawCampaignId?: string,
  timeoutMs = 5000,
) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasProducts, setHasProducts] = useState(false)
  const [products, setProducts] = useState<InsiderProductType[]>([])
  const [variationId, setVariationId] = useState<number | null>(null)

  useEffect(() => {

    setIsLoading(true)
    setHasProducts(false)
    setProducts([])
    setVariationId(null)

    if (!rawCampaignId || typeof window === 'undefined') {
      setIsLoading(false)
      return
    }
    const numericId = Number(rawCampaignId)
    if (isNaN(numericId)) {
      setIsLoading(false)
      return
    }

    // 1) LOG sessionStorage
    const storageKey = `insiderCampaignData_${numericId}`
    const raw = sessionStorage.getItem(storageKey)
    if (raw) {
      try {
        const data = JSON.parse(raw) as InsiderCampaignData
        setProducts(data.products || [])
        setHasProducts((data.products?.length ?? 0) > 0)
        setVariationId(data.variationId ?? null)
        setIsLoading(false)
        return
      } catch (err) {
        console.error(
          `Erro ao parsear sessionStorage[${storageKey}]:`,
          err
        )
      }
    }

    // 2) early listener global __insiderCampaignData
    const globalData = (window as any).__insiderCampaignData as
      | InsiderCampaignData
      | undefined
    if (globalData?.campaignId === numericId) {
      setProducts(globalData.products)
      setHasProducts(globalData.products.length > 0)
      setVariationId(globalData.variationId ?? null)
      setIsLoading(false)
      return
    }

    // 3) evento customizado (caso você tenha disparado insider-campaign-loaded-<id>)
    const eventName = `insider-campaign-loaded-${numericId}`
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail as InsiderCampaignData
      setProducts(detail.products)
      setHasProducts(detail.products.length > 0)
      setVariationId(detail.variationId ?? null)
      setIsLoading(false)
      window.removeEventListener(eventName, onCustom)
    }
    window.addEventListener(eventName, onCustom)

    // 4) fallback SDK Insider
    const insider = (window as any).Insider
    let timeoutId: number
    if (insider?.eventManager) {
      insider.eventManager.once(
        'ins-sr:only-api-campaign:load',
        (_e:any, data: InsiderCampaignData) => {

          if (data.campaignId === numericId) {
            setProducts(data.products)
            setHasProducts(data.products.length > 0)
            setVariationId(data.variationId ?? null)
          }
          setIsLoading(false)
        }
      )
      if (typeof insider.recommendation?.load === 'function') {
        insider.recommendation.load({ campaignId: numericId })
      }
      timeoutId = window.setTimeout(() => {
        setIsLoading(false)
        window.removeEventListener(eventName, onCustom)
      }, timeoutMs)
    } else {
      setIsLoading(false)
    }

    return () => {
      window.removeEventListener(eventName, onCustom)
      if (timeoutId) clearTimeout(timeoutId)
      if (insider?.eventManager) {
        insider.eventManager.off(
          'ins-sr:only-api-campaign:load',
          () => {}
        )
      }
    }
  }, [rawCampaignId, timeoutMs])

  const idsListJson = JSON.stringify(products.map((p) => p.item_id))
  return { isLoading, hasProducts, products, variationId, idsListJson }
}
